import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, toRefs, useAttrs, useSlots, watch, getCurrentInstance, provide } from '@nuxtjs/composition-api';
import { useId } from '~/core/composables/useId';
import type { Props as IconProps } from '~/core/components/base/image/SvgThemedIcon.vue';
import type { OptionOrGroup } from '~/core/types/affility/components/composed/form-item/select/OptionOrGroup';
import type { Option } from '~/core/types/affility/components/composed/form-item/select/Option';

interface Props {
    name?: string,
    placeholder?: string,
    label?: string,
    info?: string,
    error?: string,
    required?: boolean,
    disabled?: boolean,
    icon?: IconProps,
    size?: 's' | 'm' | 'l',
    inputAttrs?: Record<string, unknown>,
    value?: unknown,
    options?: OptionOrGroup[],
    dropdownPopperClass?: string;
    placement?: 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left' | 'left-start' | 'left-end' | 'right' | 'right-start' | 'right-end',
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Select',
  props: {
    name: null,
    placeholder: null,
    label: null,
    info: null,
    error: null,
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    icon: null,
    size: { default: 'm' },
    inputAttrs: null,
    value: null,
    options: { default: () => [] },
    dropdownPopperClass: null,
    placement: { default: 'bottom-start' }
  },
  emits: ['icon-click', 'input', 'change'],
  setup(__props: any, { emit: $emit }) {

const props = __props as { name?: string, placeholder?: string, label?: string, info?: string, error?: string, required: boolean, disabled: boolean, icon?: IconProps, size: 's' | 'm' | 'l', inputAttrs?: Record<string, unknown>, value?: unknown, options: OptionOrGroup[], dropdownPopperClass?: string, placement: 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left' | 'left-start' | 'left-end' | 'right' | 'right-start' | 'right-end' };





const {
    name,
    placeholder,
    label,
    info,
    error,
    required,
    disabled,
    size,
    icon,
    inputAttrs,
    value,
    placement,
    options,
} = toRefs(props);

const optionsInSlots = ref([]);

provide('select', {
    addOption,
    removeOption,
    updateOption,
});

const allOptions = computed<OptionOrGroup[]>(() => {
    return options.value.concat(optionsInSlots.value);
});

const popoverVisible = ref(false);
const internalValue = ref(value.value);
const selectedOption = ref(findOptionByValue(value.value, allOptions.value));

watch(allOptions, (val: OptionOrGroup[]) => {
    selectedOption.value = findOptionByValue(internalValue.value, val);
});
watch(internalValue, (newValue) => {
    if (newValue !== value.value)
    selectedOption.value = findOptionByValue(newValue, allOptions.value);
    $emit('input', newValue);
});
watch(value, (newValue) => {
    if (newValue !== internalValue.value) {
        internalValue.value = newValue;
        selectedOption.value = findOptionByValue(newValue, allOptions.value);
    }
});

const id = useId();
const componentId = computed(() => {
    const attrs = useAttrs();
    return attrs.id ? String(attrs.id) : id;
});
const infoId = computed(() => info.value || useSlots()['info-text'] ? componentId.value + '_info' : undefined);
const errorId = computed(() => error.value || useSlots()['error-text'] ? componentId.value + '_error' : undefined);
const selectedLabel = computed(() => getPropsObject(selectedOption.value)?.title || internalValue.value?.toString());

function findOptionByValue(value: unknown, options: OptionOrGroup[]) {
    for (const optionOrGroup of options) {
        const option = getPropsObject(optionOrGroup);
        if (option instanceof Array) {
            findOptionByValue(value, option);
        } else {
            if (typeof option.value !== 'undefined' && option.value === value ||
                option === value
            ) {
                return option;
            }
        }
    }
    return null;
};

function getPropsObject<T>(item: T): Exclude<T, string> {
    if (typeof item === 'string') {
        return { value: item, title: item, key: item } as Exclude<T, string>;
    }
    return item as Exclude<T, string>;
};

function selectOption(option: Option) {
    popoverVisible.value = false;
    internalValue.value = typeof option === 'string' ? option : option.value;
    $emit('change', option);
};

function clearDocumentSelectedText(e) {
    popoverVisible.value = true;
    e.preventDefault();
    if(window.getSelection) {
        const selection = window.getSelection();
        selection.removeAllRanges();
    }
};

function addOption(option) {
    optionsInSlots.value = optionsInSlots.value.concat([option]);
}

function removeOption(option) {
    optionsInSlots.value = optionsInSlots.value.filter((v) => v !== option);
}

function updateOption(newOption, oldOption) {
    removeOption(oldOption);
    addOption(newOption);
}


return { __sfc: true,$emit, props, name, placeholder, label, info, error, required, disabled, size, icon, inputAttrs, value, placement, options, optionsInSlots, allOptions, popoverVisible, internalValue, selectedOption, id, componentId, infoId, errorId, selectedLabel, findOptionByValue, getPropsObject, selectOption, clearDocumentSelectedText, addOption, removeOption, updateOption }
}

})